
import Vue from 'vue';
import { db } from "@/firebase";
import { RUNNIT_NODE_DEF_TAG_TYPE, RUNNIT_TYPE, RunnitNodeDef, RunnitNodeDefTag } from "@run-diffusion/shared";
import { RunnitsCRUDMixin } from "@/mixins";
import { SNACKBAR_STATUS } from "@/constants/constants";
import { mapActions, mapState } from "vuex";
import { ModelCRUDMixin } from "@/mixins/ModelCRUDMixin";
import EmptyState from '@/components/states/EmptyState.vue';
import GetSupportBtn from "@/components/Toolbar/GetSupportBtn/GetSupportBtn.vue";
import {
	getTrainingOnboardingDismissed,
	setTrainingOnboardingDismissed,
	removeTrainingOnboardingDismissed,
} from '@/utils/localStorage';
import {
	RUNNITS_OWNER_SELECTION,
	TRAINING_QUALITY_CARDS,
	TRAINING_TYPE_CARDS,
} from '@/views/Runnits/constants';
import _sortBy from 'lodash/sortBy';
import OptionCard from "@/views/Runnits/base/OptionCard.vue";
import { ROUTER } from "@/router/constants";
import BackButton from "@/components/base/BackButton.vue";
import RunnitTokens from '@/views/Runnits/RunnitTokens.vue';
import TrainOnboardingTopCard from './TrainOnboardingTopCard.vue';
import { TRAINING_ONBOARDING_DATA } from './constants';

export default Vue.extend({
	name: 'RunnitTrainModelOptions',
	metaInfo: {
		titleTemplate: 'Train',
	},
	mixins: [
		RunnitsCRUDMixin,
		ModelCRUDMixin,
	],
	data () {
		return {
			ROUTER,
			selectedTypeTagId: null,
			selectedQualityTagId: null,
			nodeDefIdSelected: null,
			trainingTypeTags: [],
			trainingQualityTags: [],
			TRAINING_QUALITY_CARDS,
			TRAINING_TYPE_CARDS,
			trainingOptionStep: 1,
			qualityTagToNodeDefMap: {},
			trainingOnboardingCardDismissed: getTrainingOnboardingDismissed(),
			trainingOnboardingData: TRAINING_ONBOARDING_DATA,
		};
	},
	computed: {
		...mapState([
			'team',
			'runnitState',
			'loadingTrainerNodeDefs',
			'trainerNodeDefs',
			'publicRunnitNodeDefTagsMap',
			'loadingPublicRunnitNodeDefTags'
		]),
		goTrainDisabled () {
			return !!(
				!this.nodeDefIdSelected ||
				(
					this.runnitState.runnitsOwnerSelection === RUNNITS_OWNER_SELECTION.TEAM &&
					!this.team
				)
			);
		},
		computedActionIslandWidth () {
			let width: string = '96.5%';

			if (this.$vuetify.breakpoint.smOnly) {
				width = '50%';
			}

			if (this.$vuetify.breakpoint.mdOnly) {
				width = '35%';
			}

			if (this.$vuetify.breakpoint.lgAndUp) {
				width = '25%';
			}

			return width;
		},
	},
	watch: {
		publicRunnitNodeDefTagsMap: {
			immediate: true,
			handler (newVal: Record<string, RunnitNodeDefTag>, oldVal: Record<string, RunnitNodeDefTag>) {
				if (newVal !== oldVal) {
					this.initTrainingTypeTagSelections(newVal, this.trainerNodeDefs);
				}
			},
		},
		trainerNodeDefs: {
			immediate: true,
			handler (newVal: RunnitNodeDef[], oldVal: RunnitNodeDef[]) {
				if (newVal !== oldVal) {
					this.initTrainingTypeTagSelections(this.publicRunnitNodeDefTagsMap, newVal);
				}
			},
		},
		selectedTypeTagId: {
			immediate: true,
			handler (newVal: string, oldVal: string) {
				if (newVal && newVal !== oldVal) {
					this.initTrainingQualityTagSelections(this.publicRunnitNodeDefTagsMap, this.trainerNodeDefs, newVal);
				}
			},
		},
	},
	methods: {
		...mapActions([
			'updateSnackbar',
		]),
		initTrainingTypeTagSelections (publicRunnitNodeDefTagsMap: Record<string, RunnitNodeDefTag>, trainerNodeDefs: RunnitNodeDef[]) {
			const trainingTypeTagIdsSet: Set<string> = new Set();
			(trainerNodeDefs || []).forEach((nodeDef: RunnitNodeDef) => {
				const tagIds: string[] = Object.keys(nodeDef.tags || {});
				tagIds.forEach((tagId: string) => {
					const tag: RunnitNodeDefTag = publicRunnitNodeDefTagsMap[tagId] || null;
					if (tag && tag.type === RUNNIT_NODE_DEF_TAG_TYPE.TRAINING_TYPE) {
						trainingTypeTagIdsSet.add(tagId);
					}
				});
			});

			this.trainingTypeTags = _sortBy(
				Array.from(trainingTypeTagIdsSet).map((tagId: string) => publicRunnitNodeDefTagsMap[tagId]),
				'sortOrder',
			);

			// Auto-selecting logic
			if (this.$vuetify.breakpoint.smAndDown) {
				this.setTypeSelected(null);
			} else if (
				!this.selectedTypeTagId ||
				!trainingTypeTagIdsSet.has(this.selectedTypeTagId)
			) {
				const autoSelectTypeTag: RunnitNodeDefTag = this._get(this.trainingTypeTags, '[0]') || null;
				if (
					autoSelectTypeTag &&
					TRAINING_TYPE_CARDS[autoSelectTypeTag.id] &&
					!TRAINING_TYPE_CARDS[autoSelectTypeTag.id].comingSoon
				) {
					this.setTypeSelected(autoSelectTypeTag.id);
				} else {
					this.setTypeSelected(null);
				}
			} else {
				this.setTypeSelected(this.selectedTypeTagId);
			}
		},
		initTrainingQualityTagSelections (publicRunnitNodeDefTagsMap: Record<string, RunnitNodeDefTag>, trainerNodeDefs: RunnitNodeDef[], selectedTypeTagId: string) {
			const trainingQualityTagIdsSet: Set<string> = new Set();
			this.qualityTagToNodeDefMap = {};

			(trainerNodeDefs || []).forEach((nodeDef: RunnitNodeDef) => {
				if (!this._get(nodeDef.tags, selectedTypeTagId)) return; // Filter to only trainer tools that have the selected training type tag

				const tagIds: string[] = Object.keys(nodeDef.tags || {});
				tagIds.forEach((tagId: string) => {
					const tag: RunnitNodeDefTag = publicRunnitNodeDefTagsMap[tagId] || null;
					if (tag && tag.type === RUNNIT_NODE_DEF_TAG_TYPE.TRAINING_QUALITY) {
						trainingQualityTagIdsSet.add(tagId);
						this.qualityTagToNodeDefMap[tagId] = nodeDef;
					}
				});
			});

			this.trainingQualityTags = _sortBy(
				Array.from(trainingQualityTagIdsSet).map((tagId: string) => publicRunnitNodeDefTagsMap[tagId]),
				'sortOrder',
			);

			// Auto-selecting logic
			if (this.$vuetify.breakpoint.smAndDown) {
				this.setQualitySelected(null);
			} else if (
				!this.selectedQualityTagId ||
				!trainingQualityTagIdsSet.has(this.selectedQualityTagId)
			) {
				// Find the cheapest quality tag
				const validQualityTags: RunnitNodeDefTag[] = this.trainingQualityTags.filter(tag =>
					TRAINING_QUALITY_CARDS[tag.id] &&
					!TRAINING_QUALITY_CARDS[tag.id].comingSoon
				);

				// Sort by cost and get the cheapest one
				const autoSelectQualityTag: RunnitNodeDefTag = validQualityTags.length > 0
					? _sortBy(
						validQualityTags,
						tag => this.qualityTagToNodeDefMap[tag.id]?.costPerResult || Infinity
					)[0]
					: null;

				if (autoSelectQualityTag) {
					this.setQualitySelected(autoSelectQualityTag.id);
				} else {
					this.setQualitySelected(null);
				}
			} else {
				this.setQualitySelected(this.selectedQualityTagId);
			}
		},
		setTypeSelected (tagId: string) {
			if (tagId !== this.selectedTypeTagId) {
				this.selectedTypeTagId = tagId;
				if (tagId) {
					this.trainingOptionStep = 2;
				}
			}
		},
		setQualitySelected (tagId: string) {
			this.selectedQualityTagId = tagId;
			this.nodeDefIdSelected = null;
			if (tagId) {
				this.trainingOptionStep = 3;
				const nodeDef: RunnitNodeDef = this.qualityTagToNodeDefMap[tagId] || null;
				this.nodeDefIdSelected = nodeDef ? nodeDef.id : null;
			}
		},
		async goTrain () {
			if (!this.nodeDefIdSelected) {
				this.updateSnackbar({
					status: SNACKBAR_STATUS.ERROR,
					message: 'Please select a trainer to use',
					show: true,
				});
				return;
			}

			let query = db.doc(`runnitNodeDefs/${this.nodeDefIdSelected}`);
			const runnitNodeDef: RunnitNodeDef = await query.get().then(doc => ({
				id: doc.id,
				...doc.data()
			} as RunnitNodeDef));
			await this.onAddRunnitClick(runnitNodeDef, RUNNIT_TYPE.SINGLE_TOOL);
		},
		async goToModelsListPageWithUploadDialogOpen () {
			await this.routerPush(this.$route, this.$router, { name: ROUTER.RUNNITS_MODELS_LIST });
			await this.onAddModelClick()
		},
		onBackClick () {
			this.routerPush(this.$route, this.$router, { name: ROUTER.RUNNITS_MODELS_LIST });
		},
		toggleTrainingOnboardingCard (nodeDefId: string) {
			this.trainingOnboardingCardDismissed = !this.trainingOnboardingCardDismissed;
			if (this.trainingOnboardingCardDismissed) {
				setTrainingOnboardingDismissed();
			}
		},
		showTrainingOnboardingCard () {
			this.trainingOnboardingCardDismissed = false;
			removeTrainingOnboardingDismissed();
		},
	},
	components: {
		RunnitTokens,
		BackButton,
		OptionCard,
		GetSupportBtn,
		EmptyState,
		TrainOnboardingTopCard,
	},
});
